import { createContext, useContext, useEffect, useState } from "react";

const en = {
  nav: {
    about: "About", services: "Services", products: "Products", catalogue: "Catalogue",
    projects: "Projects", journal: "Journal", contact: "Contact", quote: "Get a quote",
    callUs: "Call us", whatsapp: "Chat on WhatsApp", brand: "Interior • Exterior • Products",
  },
  hero: {
    eyebrow: "INT–EXT — Interior • Exterior • Products",
    l1: "Interior.", l2: "Exterior.", l3: "Products",
    sub: "25+ years crafting premium architectural material and design solutions for interior and exterior spaces. Coimbatore, India.",
    explore: "Explore products", catalogue: "View catalogue",
    stripL: "Architectural material showroom", scroll: "Scroll", stripR: "Coimbatore — 2026",
  },
  about: {
    chapter: "Chapter 01 — About us",
    h1a: "Designing spaces", h2pre: "that", h2gold: "inspire", h2post: "& elevate.",
    p: "At INT–EXT, we blend creativity with functionality to craft interior and exterior spaces that reflect your style and stand the test of time — three generations of expertise, one standard of excellence.",
    knowMore: "Know more", est: "Est. 25+ years", imgAlt: "INT–EXT interior project",
  },
  services: {
    chapter: "Chapter 02 — What we offer",
    h1a: "Crafted for", h1b: "every space",
    p: "Thoughtfully designed spaces, crafted to reflect your vision, lifestyle and needs.",
    items: [
      { name: "Interior Design", desc: "Functional, aesthetic and innovative interior spaces that reflect your personality and purpose." },
      { name: "Exterior Design", desc: "From modern facades to durable elevations — exterior solutions that combine strength, style and sustainability." },
      { name: "Complete Solutions", desc: "From concept to completion, we bring your vision to life with carefully planned, professionally executed spaces." },
    ],
  },
  marquee: {
    chapter: "Chapter 03 — Product index",
    note: "12 materials — tap to open catalogue",
    brand: "Interior • Exterior • Products",
  },
  showroom: {
    chapter: "Chapter 04 — Showroom",
    h1a: "The", h1b: "Showroom", count: "/ 12",
    p: "Drag, scroll or swipe — every material opens its catalogue page.",
    prev: "Previous products", next: "Next products", view: "View material",
  },
  projects: {
    chapter: "Chapter 05 — Recent projects",
    h1a: "Spaces we've", h1b: "brought to life",
    p: "A glimpse into the spaces we've designed, transformed and delivered.",
    items: [
      { title: "Lavender Ambient Interior", tag: "Interior" },
      { title: "Ambient Living Space", tag: "Interior" },
      { title: "Warm Contemporary Lounge", tag: "Interior" },
      { title: "Modern Residential Interior", tag: "Interior" },
    ],
  },
  why: {
    chapter: "Chapter 06 — Why INT–EXT",
    h1a: "Built on craft,", h1b: "kept on", h1gold: "promise",
    items: [
      { title: "Experienced Team", desc: "Skilled professionals with years of experience delivering quality interior and exterior solutions." },
      { title: "Quality Craftsmanship", desc: "Precision, durable materials and attention to detail in every project we undertake." },
      { title: "End-to-End Solutions", desc: "From initial concepts to final execution, we help bring your vision to life." },
      { title: "Personalized Designs", desc: "Every space is designed around your requirements, lifestyle, preferences and budget." },
      { title: "Transparent Process", desc: "Clear communication and careful planning keep you informed throughout the project." },
      { title: "Customer Satisfaction", desc: "We build lasting relationships by focusing on quality, reliability and client satisfaction." },
    ],
  },
  testimonials: {
    chapter: "Chapter 07 — What our clients say",
    note: "Coimbatore & beyond",
    quotes: [
      "They did a very good interior work to my house. The work and output they created made my dream house look a lot more beautiful.",
      "Very good work. Best interior designs ever seen.",
      "Good experience.",
      "Loved the service.",
    ],
  },
  journal: {
    chapter: "Chapter 08 — Design journal",
    h1a: "Ideas &", h1b: "inspiration",
    p: "Practical tips from the world of interior and exterior design.",
    read: "Read journal",
    posts: [
      { title: "5 Ways to Make Your Home Feel More Spacious", tags: ["Interior Design", "Inspiration"] },
      { title: "Creating a Modern Exterior That Stands Out", tags: ["Exterior Design", "Ideas"] },
      { title: "Choosing the Right Design for Your Space", tags: ["Design Tips", "Home"] },
    ],
  },
  stats: {
    chapter: "Chapter 09 — By the numbers",
    labels: ["Years of experience", "Projects completed", "Generations of expertise", "Based in Coimbatore"],
  },
  contact: {
    chapter: "Chapter 10 — Contact",
    h1a: "Let's create a", h1b: "space you'll", h1c: "love",
    p: "From concept to completion — interiors, exteriors and architectural products tailored to your vision, lifestyle and budget.",
    whatsapp: "Chat on WhatsApp", call: "Call us", phone: "Phone", wa: "WhatsApp",
  },
  footer: {
    about: "Creating thoughtfully designed interior and exterior spaces that combine functionality, elegance and quality craftsmanship.",
    servicesTitle: "Our services",
    serviceList: ["Interior Design", "Exterior Design", "Custom Design Solutions", "Renovation & Remodeling", "Product Solutions"],
    quickLinks: "Quick links", contactTitle: "Contact",
    location: "Coimbatore, Tamil Nadu", waTag: "— WhatsApp",
    rights: "© 2026 INT–EXT. All rights reserved.", brand: "Interior • Exterior • Products",
  },
  catalogue: {
    micro: "INT–EXT — Full catalogue",
    h1a: "Material", h1b: "Catalogue",
    p: "Twelve architectural material categories for interior and exterior spaces — backed by 25+ years of experience.",
    talk: "Talk to us", enquire: "Enquire on WhatsApp",
  },
  quote: {
    title: "Request a quote", subtitle: "Tell us about your project — we'll get back to you shortly.",
    name: "Your name", phone: "Phone number", interest: "I'm interested in", message: "Tell us more (optional)",
    send: "Send enquiry", sending: "Sending…", success: "Enquiry sent — we'll reach out soon.",
    error: "Something went wrong. Please try WhatsApp or a call.",
    interests: ["Interior Design", "Exterior Design", "Complete Solution", "Products / Materials"],
  },
};

const ta = {
  nav: {
    about: "எங்களை பற்றி", services: "சேவைகள்", products: "பொருட்கள்", catalogue: "பட்டியல்",
    projects: "திட்டங்கள்", journal: "இதழ்", contact: "தொடர்பு", quote: "விலை கேளுங்கள்",
    callUs: "அழைக்கவும்", whatsapp: "WhatsApp-இல் அரட்டை", brand: "உட்புறம் • வெளிப்புறம் • பொருட்கள்",
  },
  hero: {
    eyebrow: "INT–EXT — உட்புறம் • வெளிப்புறம் • பொருட்கள்",
    l1: "உட்புறம்.", l2: "வெளிப்புறம்.", l3: "பொருட்கள்",
    sub: "உட்புற மற்றும் வெளிப்புற இடங்களுக்கு 25+ ஆண்டுகளாக உயர்தர கட்டிடக்கலை பொருட்கள் மற்றும் வடிவமைப்பு தீர்வுகள். கோயம்புத்தூர், இந்தியா.",
    explore: "பொருட்களை பார்க்க", catalogue: "பட்டியலை பார்க்க",
    stripL: "கட்டிடக்கலை பொருட்கள் அரங்கம்", scroll: "ஸ்க்ரோல்", stripR: "கோயம்புத்தூர் — 2026",
  },
  about: {
    chapter: "அத்தியாயம் 01 — எங்களை பற்றி",
    h1a: "உ்வேகமூட்டும்", h2pre: "இடங்களை", h2gold: "வடிவமைக்கிறோம்", h2post: "",
    p: "INT–EXT-இல், படைப்பாற்றலை செயல்பாட்டுடன் இணைத்து, உங்கள் பாணியை பிரதிபலித்து காலத்தால் அழியாத உட்புற மற்றும் வெளிப்புற இடங்களை உருவாக்குகிறோம் — மூன்று தலைமுறை நிபுணத்துவம், ஒரே தரம்.",
    knowMore: "மேலும் அறிய", est: "25+ ஆண்டுகள் அனுபவம்", imgAlt: "INT–EXT உட்புற திட்டம்",
  },
  services: {
    chapter: "அத்தியாயம் 02 — எங்கள் சேவைகள்",
    h1a: "ஒவ்வொரு இடத்திற்கும்", h1b: "சிறப்பான வடிவமைப்பு",
    p: "உங்கள் பார்வை, வாழ்க்கை முறை மற்றும் தேவைகளை பிரதிபலிக்கும் கவனமாக வடிவமைக்கப்பட்ட இடங்கள்.",
    items: [
      { name: "உட்புற வடிவமைப்பு", desc: "உங்கள் ஆளுமை மற்றும் நோக்கத்தை பிரதிபலிக்கும் செயல்பாட்டு, அழகியல் மற்றும் புதுமையான உட்புற இடங்கள்." },
      { name: "வெளிப்புற வடிவமைப்பு", desc: "நவீன முகப்புகள் முதல் நீடித்த மேற்பரப்புகள் வரை — வலிமை, பாணி மற்றும் நிலைத்தன்மை கொண்ட வெளிப்புற தீர்வுகள்." },
      { name: "முழுமையான தீர்வுகள்", desc: "கருத்தில் இருந்து நிறைவு வரை — கவனமான திட்டமிடலுடன், தொழில்முறை செயலாக்கத்துடன் உங்கள் கனவை நனவாக்குகிறோம்." },
    ],
  },
  marquee: {
    chapter: "அத்தியாயம் 03 — பொருட்கள் அகவரிசை",
    note: "12 பொருட்கள் — பட்டியலை திறக்க தட்டவும்",
    brand: "உட்புறம் • வெளிப்புறம் • பொருட்கள்",
  },
  showroom: {
    chapter: "அத்தியாயம் 04 — அரங்கம்",
    h1a: "எங்கள்", h1b: "அரங்கம்", count: "/ 12",
    p: "இழுக்கவும், ஸ்க்ரோல் செய்யவும் அல்லது ஸ்வைப் செய்யவும் — ஒவ்வொரு பொருளும் அதன் பட்டியல் பக்கத்தை திறக்கும்.",
    prev: "முந்தைய பொருட்கள்", next: "அடுத்த பொருட்கள்", view: "பொருளை பார்க்க",
  },
  projects: {
    chapter: "அத்தியாயம் 05 — சமீபத்திய திட்டங்கள்",
    h1a: "நாங்கள் உருவாக்கிய", h1b: "இடங்கள்",
    p: "நாங்கள் வடிவமைத்து, மாற்றியமைத்து, உயிரூட்டிய இடங்களின் சிறு பார்வை.",
    items: [
      { title: "லாவெண்டர் அமைதி உட்புறம்", tag: "உட்புறம்" },
      { title: "அமைதியான வாழ்விடம்", tag: "உட்புறம்" },
      { title: "அழகிய நவீன ஓய்வறை", tag: "உட்புறம்" },
      { title: "நவீன குடியிருப்பு உட்புறம்", tag: "உட்புறம்" },
    ],
  },
  why: {
    chapter: "அத்தியாயம் 06 — ஏன் INT–EXT",
    h1a: "திறமையில் கட்டப்பட்டது,", h1b: "நம்பிக்கையில்", h1gold: "நிலைக்கும்",
    items: [
      { title: "அனுபவமிக்க குழு", desc: "தரமான உட்புற மற்றும் வெளிப்புற தீர்வுகளை வழங்கும் பல ஆண்டு அனுபவமிக்க நிபுணர்கள்." },
      { title: "தரமான கைவினை", desc: "ஒவ்வொரு திட்டத்திலும் துல்லியம், நீடித்த பொருட்கள் மற்றும் நுணுக்கமான கவனம்." },
      { title: "முழு தீர்வுகள்", desc: "ஆரம்ப கருத்து முதல் இறுதி செயலாக்கம் வரை உங்கள் கனவை நனவாக்க உதவுகிறோம்." },
      { title: "தனிப்பயன் வடிவமைப்பு", desc: "ஒவ்வொரு இடமும் உங்கள் தேவை, வாழ்க்கை முறை, விருப்பம் மற்றும் பட்ஜெட்டிற்கு ஏற்ப வடிவமைக்கப்படுகிறது." },
      { title: "வெளிப்படை செயல்முறை", desc: "தெளிவான தொடர்பு மற்றும் கவனமான திட்டமிடல் திட்டம் முழுவதும் உங்களை அறிவித்தே வைக்கும்." },
      { title: "வாடிக்கையாளர் திருப்தி", desc: "தரம், நம்பகத்தன்மை மற்றும் திருப்தியில் கவனம் செலுத்தி நீண்ட உறவுகளை உருவாக்குகிறோம்." },
    ],
  },
  testimonials: {
    chapter: "அத்தியாயம் 07 — வாடிக்கையாளர்கள் சொல்வது",
    note: "கோயம்புத்தூர் & அப்பால்",
    quotes: [
      "என் வீட்டிற்கு மிக நல்ல உட்புற வேலை செய்து கொடுத்தார்கள். அவர்களின் வேலை என் கனவு இல்லத்தை இன்னும் அழகாக்கியது.",
      "மிக நல்ல வேலை. நான் பார்த்ததிலேயே சிறந்த உட்புற வடிவமைப்புகள்.",
      "நல்ல அனுபவம்.",
      "சேவை மிகவும் பிடித்தது.",
    ],
  },
  journal: {
    chapter: "அத்தியாயம் 08 — வடிவமைப்பு இதழ்",
    h1a: "யோசனைகள் &", h1b: "உ்வேகம்",
    p: "உட்புற மற்றும் வெளிப்புற வடிவமைப்பு உலகில் இருந்து நடைமுறை குறிப்புகள்.",
    read: "படிக்க",
    posts: [
      { title: "வீட்டை விசாலமாக உணர 5 வழிகள்", tags: ["உட்புற வடிவமைப்பு", "உ்வேகம்"] },
      { title: "தனித்துவமான நவீன வெளிப்புறம் உருவாக்குதல்", tags: ["வெளிப்புற வடிவமைப்பு", "யோசனைகள்"] },
      { title: "உங்கள் இடத்திற்கு சரியான வடிவமைப்பை தேர்வு செய்தல்", tags: ["குறிப்புகள்", "வீடு"] },
    ],
  },
  stats: {
    chapter: "அத்தியாயம் 09 — எண்களில்",
    labels: ["ஆண்டுகள் அனுபவம்", "நிறைவடைந்த திட்டங்கள்", "தலைமுறை நிபுணத்துவம்", "கோயம்புத்தூரில் அமைந்துள்ளது"],
  },
  contact: {
    chapter: "அத்தியாயம் 10 — தொடர்பு",
    h1a: "உங்கள் கனவு", h1b: "இடத்தை உருவாக்குவோம்", h1c: "ஒன்றாக",
    p: "கருத்து முதல் நிறைவு வரை — உங்கள் பார்வை, வாழ்க்கை முறை மற்றும் பட்ஜெட்டிற்கு ஏற்ற உட்புறம், வெளிப்புறம் மற்றும் கட்டிடக்கலை பொருட்கள்.",
    whatsapp: "WhatsApp-இல் அரட்டை", call: "அழைக்கவும்", phone: "தொலைபேசி", wa: "வாட்ஸ்அப்",
  },
  footer: {
    about: "செயல்பாடு, நேர்த்தி மற்றும் தரமான கைவினையை இணைக்கும் சிந்தனையுடன் வடிவமைக்கப்பட்ட உட்புற மற்றும் வெளிப்புற இடங்களை உருவாக்குகிறோம்.",
    servicesTitle: "எங்கள் சேவைகள்",
    serviceList: ["உட்புற வடிவமைப்பு", "வெளிப்புற வடிவமைப்பு", "தனிப்பயன் தீர்வுகள்", "புதுப்பித்தல் & மாற்றம்", "பொருட்கள் தீர்வுகள்"],
    quickLinks: "விரைவு இணைப்புகள்", contactTitle: "தொடர்பு",
    location: "கோயம்புத்தூர், தமிழ்நாடு", waTag: "— வாட்ஸ்அப்",
    rights: "© 2026 INT–EXT. அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.", brand: "உட்புறம் • வெளிப்புறம் • பொருட்கள்",
  },
  catalogue: {
    micro: "INT–EXT — முழு பட்டியல்",
    h1a: "பொருட்கள்", h1b: "பட்டியல்",
    p: "உட்புற மற்றும் வெளிப்புற இடங்களுக்கான பன்னிரண்டு கட்டிடக்கலை பொருட்கள் வகைகள் — 25+ ஆண்டுகள் அனுபவத்துடன்.",
    talk: "பேசுங்கள்", enquire: "WhatsApp-இல் விசாரிக்க",
  },
  quote: {
    title: "விலை கேளுங்கள்", subtitle: "உங்கள் திட்டம் பற்றி சொல்லுங்கள் — விரைவில் தொடர்பு கொள்வோம்.",
    name: "உங்கள் பெயர்", phone: "தொலைபேசி எண்", interest: "எனக்கு தேவை", message: "மேலும் விவரம் (விருப்பம்)",
    send: "விசாரணை அனுப்பு", sending: "அனுப்புகிறது…", success: "விசாரணை அனுப்பப்பட்டது — விரைவில் தொடர்பு கொள்வோம்.",
    error: "ஏதோ தவறு நடந்தது. WhatsApp அல்லது அழைப்பை முயற்றிக்கவும்.",
    interests: ["உட்புற வடிவமைப்பு", "வெளிப்புற வடிவமைப்பு", "முழுமையான தீர்வு", "பொருட்கள் / மெட்டீரியல்"],
  },
};

const DICT = { en, ta };
const LangCtx = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("intext-lang") || "en");

  useEffect(() => {
    localStorage.setItem("intext-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (path) => {
    const resolve = (d) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), d);
    return resolve(DICT[lang]) ?? resolve(DICT.en) ?? path;
  };

  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);
